import React from 'react';
import { BarChart2, Calculator, Wallet, Percent } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров налогового вычета
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  deductionTypes
}) => {
  // Расчет текущего дохода
  const incomeAmount = parseFloat(formData.incomeAmount) || 0;
  
  // Расчет текущей ставки НДФЛ
  const taxRate = parseFloat(formData.taxRate) || 13;
  
  // Теоретический годовой НДФЛ
  const annualTax = incomeAmount * (taxRate / 100);
  
  // Обработчик изменения дохода
  const handleIncomeChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      incomeAmount: newValue
    }));
  };
  
  // Обработчик изменения ставки НДФЛ
  const handleTaxRateChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      taxRate: newValue
    }));
  };
  
  // Обработчик изменения типа вычета
  const handleDeductionTypeChange = (newType) => {
    setFormData(prev => ({
      ...prev,
      deductionType: newType
    }));
  };
  
  // Функция расчета
  const calculateDeduction = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Предопределенные суммы дохода для быстрого выбора
  const predefinedIncomes = [
    { value: '500000', label: '500 тыс.' },
    { value: '1000000', label: '1 млн' },
    { value: '2000000', label: '2 млн' },
    { value: '5000000', label: '5 млн' }
  ];
  
  // Предопределенные ставки налога
  const predefinedTaxRates = [
    { value: '13', label: '13%' },
    { value: '15', label: '15%' },
    { value: '30', label: '30%' },
    { value: '35', label: '35%' }
  ];
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-emerald-500" />
        Быстрая настройка параметров
      </h3>
      
      <div className="space-y-5">
        {/* Годовой доход */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="incomeAmountSlider" className="text-gray-600 dark:text-gray-400">Годовой доход</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(incomeAmount)}</span>
          </div>
          <input
            type="range"
            id="incomeAmountSlider"
            min="100000"
            max="10000000"
            step="100000"
            value={incomeAmount}
            onChange={handleIncomeChange}
            aria-label="Годовой доход"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Годовой НДФЛ */}
          <div className="mt-1.5 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center">
              <Wallet className="w-3.5 h-3.5 mr-1" /> Доход
            </span>
            <span className="flex items-center">
              <Percent className="w-3.5 h-3.5 mr-1" /> НДФЛ: {formatCurrency(annualTax)}
            </span>
          </div>
          
          {/* Кнопки с предопределенными доходами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedIncomes.map(income => (
              <button
                key={income.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, incomeAmount: income.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.incomeAmount === income.value 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {income.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Ставка НДФЛ */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="taxRateSlider" className="text-gray-600 dark:text-gray-400">Ставка НДФЛ</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.taxRate}%</span>
          </div>
          <input
            type="range"
            id="taxRateSlider"
            min="5"
            max="35"
            step="1"
            value={taxRate}
            onChange={handleTaxRateChange}
            aria-label="Ставка НДФЛ (%)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Кнопки с предопределенными ставками */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedTaxRates.map(rate => (
              <button
                key={rate.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, taxRate: rate.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.taxRate === rate.value 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {rate.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Тип вычета */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Выберите тип вычета:
          </div>
          <div className="grid grid-cols-1 gap-2">
            {deductionTypes.slice(0, 5).map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => handleDeductionTypeChange(type.id)}
                className={`p-2 text-left rounded-lg border transition-colors
                  ${formData.deductionType === type.id 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium text-sm">{type.name}</div>
                {type.maxDeduction && (
                  <div className="text-xs opacity-75 mt-1">
                    До {new Intl.NumberFormat('ru-RU').format(type.maxDeduction)} ₽ возврата
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка запуска расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateDeduction}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать вычет
        </Button>
      </div>

      {/* Информационная подсказка */}
      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg">
        <p className="text-xs text-gray-600 dark:text-gray-400">
          Налоговые вычеты позволяют вернуть часть уплаченного НДФЛ. Максимальная сумма возврата ограничена 
          суммой реально уплаченного налога. Для оптимального использования настройте параметры под вашу ситуацию.
        </p>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;